import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Copy, Check, RefreshCw, Wand2, Image as ImageIcon, Download, Instagram, Linkedin, Twitter, Facebook, Music2, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ModuleAccent from "@/components/ModuleAccent";

const PRIMARY = "#7C3AED";
const ACCENT = "#FF1A5C";

const PLATFORMS = [
    { id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C" },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
    { id: "x", label: "X", icon: Twitter, color: "#000000" },
    { id: "tiktok", label: "TikTok", icon: Music2, color: "#FE2C55" },
    { id: "threads", label: "Threads", icon: MessageCircle, color: "#666666" },
];

export default function TotalKlickADSCopyStudio() {
    const { brandId } = useParams();
    const [brand, setBrand] = useState(null);
    const [brief, setBrief] = useState("");
    const [selected, setSelected] = useState(["instagram", "linkedin"]);
    const [generating, setGenerating] = useState(false);
    const [output, setOutput] = useState(null);
    const [error, setError] = useState("");
    const [copiedKey, setCopiedKey] = useState(null);
    // Image generation state
    const [imageFormat, setImageFormat] = useState("square");
    const [generatingImage, setGeneratingImage] = useState(false);
    const [image, setImage] = useState(null);
    const [imageError, setImageError] = useState("");

    useEffect(() => {
        api.get(`/ads/brands/${brandId}`).then(({ data }) => setBrand(data.brand)).catch((e) => setError(formatApiError(e)));
    }, [brandId]);

    const togglePlatform = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    };

    const generate = async () => {
        if (!brief.trim() || selected.length === 0) return;
        setGenerating(true); setError(""); setOutput(null);
        try {
            const { data } = await api.post(`/ads/brands/${brandId}/generate-copy`, {
                brief, platforms: selected,
            });
            setOutput(data);
        } catch (e) { setError(formatApiError(e)); }
        setGenerating(false);
    };

    const copyToClipboard = (key, text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        });
    };

    const generateImage = async () => {
        if (!brief.trim()) return;
        setGeneratingImage(true); setImageError(""); setImage(null);
        try {
            const { data } = await api.post(`/ads/brands/${brandId}/generate-image`, {
                brief, format: imageFormat,
            });
            setImage(data);
        } catch (e) { setImageError(formatApiError(e)); }
        setGeneratingImage(false);
    };

    return (
        <ModuleAccent module="ads" as="div" style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#0A1024", color: "#fff", minHeight: "100vh" }}>
            <header className="px-6 md:px-10 py-3.5 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(10,16,36,0.92)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <Link to={`/totalklickads/brand/${brandId}`} className="flex items-center gap-2 text-[13px] font-bold hover:opacity-70"><ArrowLeft size={14} /> Brand</Link>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(124,58,237,0.18)" }}>
                    <Wand2 size={13} style={{ color: PRIMARY }} />
                    <span className="mono text-[10.5px] uppercase tracking-[0.15em] font-extrabold" style={{ color: "#C4B5FD" }}>Copy Studio AI</span>
                </div>
                <span className="w-[80px]" />
            </header>

            <section className="px-6 md:px-10 py-8 max-w-5xl mx-auto">
                <h1 className="text-[28px] md:text-[36px] font-extrabold tracking-tighter">
                    AI Copy Studio · <span style={{ color: PRIMARY }}>{brand?.name || "..."}</span>
                </h1>
                <p className="text-[13.5px] opacity-70 mt-1">L&apos;AI usa il Brand DNA per generare copy adattato a ogni social.</p>

                <div className="mt-6 rounded-2xl p-5" style={{ background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.18)" }}>
                    <label className="mono text-[10px] uppercase tracking-wider font-bold mb-2 block" style={{ color: "#C4B5FD" }}>Brief / idea del post *</label>
                    <textarea
                        value={brief}
                        onChange={(e) => setBrief(e.target.value)}
                        placeholder="Es: lancio del nuovo menu primaverile con piatti vegani. Vogliamo enfatizzare la sostenibilità degli ingredienti km0."
                        rows="3"
                        className="w-full px-3 py-2.5 rounded-lg text-[14px] outline-none resize-none"
                        style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" }}
                        data-testid="copy-brief"
                    />
                    <div className="mt-4">
                        <label className="mono text-[10px] uppercase tracking-wider font-bold mb-2 block" style={{ color: "#C4B5FD" }}>Piattaforme</label>
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map(p => {
                                const Ico = p.icon;
                                const active = selected.includes(p.id);
                                return (
                                    <button key={p.id} type="button" onClick={() => togglePlatform(p.id)} className="px-3 py-1.5 rounded-full text-[12px] font-bold transition inline-flex items-center gap-1.5" style={{ background: active ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` : "rgba(255,255,255,0.06)", color: active ? "#fff" : "#C4B5FD", border: active ? "none" : "1px solid rgba(255,255,255,0.12)" }} data-testid={`copy-platform-${p.id}`}>
                                        <Ico size={12} /> {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <button onClick={generate} disabled={generating || !brief.trim() || selected.length === 0} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full font-extrabold text-[14px] text-white disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, boxShadow: `0 12px 32px ${PRIMARY}45` }} data-testid="copy-generate">
                        {generating ? (<><RefreshCw size={14} className="animate-spin" /> Generazione...</>) : (<><Sparkles size={14} /> Genera con AI</>)}
                    </button>
                    {error && <div className="mt-3 p-2.5 rounded-lg text-[12.5px]" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }} data-testid="copy-error">{error}</div>}
                </div>

                {/* IMAGE GENERATOR */}
                <div className="mt-6 rounded-2xl p-5" style={{ background: "rgba(255,107,26,0.05)", border: "1px solid rgba(255,107,26,0.18)" }}>
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                        <div>
                            <h3 className="text-[14px] font-extrabold flex items-center gap-2"><ImageIcon size={14} style={{ color: "#FF6B1A" }} /> Genera immagine brand-coded</h3>
                            <p className="text-[11px] opacity-60 mt-0.5">Stesso brief → immagine via Nano Banana, con la palette del Brand DNA.</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                            {[{ v: "square", l: "1:1" }, { v: "portrait", l: "4:5" }, { v: "story", l: "9:16" }, { v: "landscape", l: "16:9" }].map(f => (
                                <button key={f.v} type="button" onClick={() => setImageFormat(f.v)} className="px-2.5 py-1 rounded-md text-[11px] font-extrabold mono" style={{ background: imageFormat === f.v ? "linear-gradient(135deg,#FF6B1A,#FF1A5C)" : "rgba(255,255,255,0.06)", color: imageFormat === f.v ? "#fff" : "#FED7AA", border: imageFormat === f.v ? "none" : "1px solid rgba(255,255,255,0.1)" }} data-testid={`img-fmt-${f.v}`}>{f.l}</button>
                            ))}
                        </div>
                    </div>
                    <button onClick={generateImage} disabled={generatingImage || !brief.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-extrabold text-[13px] text-white disabled:opacity-50" style={{ background: "linear-gradient(135deg, #FF6B1A, #FF1A5C)" }} data-testid="img-generate">
                        {generatingImage ? (<><RefreshCw size={13} className="animate-spin" /> Generazione (~15s)...</>) : (<><Wand2 size={13} /> Genera immagine</>)}
                    </button>
                    {imageError && <div className="mt-3 p-2.5 rounded-lg text-[12.5px]" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>{imageError}</div>}
                    {image && (
                        <div className="mt-4 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.1)" }} data-testid="img-result">
                            <div className="px-3 py-2 flex items-center justify-between flex-wrap gap-2" style={{ background: "rgba(255,255,255,0.04)" }}>
                                <span className="mono text-[10.5px] uppercase tracking-wider opacity-70">Formato {image.format} · salvata in Libreria</span>
                                <div className="flex items-center gap-2">
                                    {image.asset_id && (
                                        <Link to={`/totalklickads/brand/${brandId}/library?edit=${image.asset_id}`} className="inline-flex items-center gap-1 text-[11.5px] font-bold opacity-90 hover:opacity-100" data-testid="img-open-overlay" style={{ color: "#C4B5FD" }}>
                                            ✦ Aggiungi testo
                                        </Link>
                                    )}
                                    <a href={image.data_uri} download={`brand-${brandId}-${image.format}.png`} className="inline-flex items-center gap-1 text-[11.5px] font-bold opacity-80 hover:opacity-100" data-testid="img-download">
                                        <Download size={11} /> Scarica
                                    </a>
                                    <Link to={`/totalklickads/brand/${brandId}/library`} className="inline-flex items-center gap-1 text-[11.5px] font-bold opacity-80 hover:opacity-100" data-testid="img-open-library" style={{ color: "#C4B5FD" }}>
                                        Libreria →
                                    </Link>
                                </div>
                            </div>
                            <img src={image.data_uri} alt="AI generated" className="w-full max-h-[480px] object-contain" style={{ background: "#0a0a0a" }} />
                        </div>
                    )}
                </div>

                {/* OUTPUT */}
                {output && (
                    <div className="mt-8 space-y-4" data-testid="copy-output">
                        <h2 className="text-[18px] font-extrabold">Output AI</h2>
                        {output.platforms.map(pid => {
                            const platform = PLATFORMS.find(p => p.id === pid);
                            const post = output.posts[pid];
                            if (!post || !platform) return null;
                            const Ico = platform.icon;
                            const fullText = post.copy + (post.hashtags?.length ? "\n\n" + post.hashtags.join(" ") : "");
                            return (
                                <div key={pid} className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`copy-card-${pid}`}>
                                    <div className="px-5 py-3 flex items-center justify-between" style={{ background: `${platform.color}15`, borderBottom: `1px solid ${platform.color}30` }}>
                                        <div className="flex items-center gap-2">
                                            <Ico size={16} style={{ color: platform.color }} />
                                            <span className="font-extrabold text-[14px]">{platform.label}</span>
                                            <span className="text-[10.5px] opacity-50 mono">{fullText.length} car.</span>
                                        </div>
                                        <button onClick={() => copyToClipboard(pid, fullText)} className="inline-flex items-center gap-1 text-[11.5px] font-bold opacity-80 hover:opacity-100" data-testid={`copy-button-${pid}`}>
                                            {copiedKey === pid ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> Copia</>}
                                        </button>
                                    </div>
                                    <div className="p-5">
                                        <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{post.copy}</p>
                                        {post.hashtags?.length > 0 && (
                                            <p className="mt-3 text-[12.5px]" style={{ color: platform.color }}>{post.hashtags.join(" ")}</p>
                                        )}
                                        {post.cta && (
                                            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold" style={{ background: `${platform.color}20`, color: platform.color }}>
                                                CTA: {post.cta}
                                            </div>
                                        )}
                                        {post.rationale && (
                                            <details className="mt-3">
                                                <summary className="text-[11px] opacity-50 cursor-pointer hover:opacity-80">Perché funziona</summary>
                                                <p className="mt-2 text-[12px] opacity-70 italic">{post.rationale}</p>
                                            </details>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="text-center pt-4">
                            <button onClick={generate} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold opacity-70 hover:opacity-100" data-testid="copy-regenerate">
                                <RefreshCw size={12} /> Rigenera con lo stesso brief
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </ModuleAccent>
    );
}
